import { computed, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import { useRouter } from 'vue-router';
import { findPermission, hasAccessToFeatures } from '@/composables/User';
import { StatsType } from '@/types';
import type { RowActionOption } from '@/components/table-rest-api/types';
import type { UsersPagesConfiguration, UsersPagesConfigurationUpdateInputItem } from '@/types/generated-types/graphql';

/**
 * Options for useStatsActions composable
 */
interface UseStatsActionsOptions {
  activeTab: Ref<StatsType>;
  selectedReport: Ref<number | null>;
  showRenameOverlay: (config: UsersPagesConfigurationUpdateInputItem, event: Event) => void;
  duplicateConfirmationDialog: (config: UsersPagesConfiguration) => void;
  deleteConfirmationDialog: (config: UsersPagesConfigurationUpdateInputItem) => void;
}

/**
 * Composable that provides row actions for stats reports list
 * Actions: View, Send Test, Edit, Duplicate, Rename, Delete
 */
export default function useStatsActions(options: UseStatsActionsOptions) {
  const { t } = useI18n();
  const store = useStore();
  const router = useRouter();

  const featurePermissionEdit = hasAccessToFeatures('stats_report.edit');

  /**
   * Row actions - triggered from individual row menu
   * Order: View, Send Test, Edit, Duplicate, Rename, Delete
   */
  const rowActions = computed<RowActionOption[]>(() => {
    const actions: (RowActionOption | undefined)[] = [
      // View - only for Stats tab
      {
        label: t('stats.actions.view'),
        icon: 'far fa-eye',
        command: (usersPagesConfig: any) => {
          const viewStats = () => {
            router.push({
              name: 'custom_stats',
              params: { pageId: usersPagesConfig.id },
            });
          };

          if (usersPagesConfig.owner === 'user') {
            if (featurePermissionEdit && !featurePermissionEdit.access) {
              store.commit('general/setIsFeatureUnavailableModalVisible', true);
              store.commit('general/setFeatureUnavailableMinPlan', featurePermissionEdit.minimumPlan);
              store.commit('general/setOnAuthorizedClickFunction', viewStats);
            } else {
              viewStats();
            }
          } else if (usersPagesConfig.owner === 'system') {
            viewStats();
          }
        },
        visible: () => options.activeTab.value !== StatsType.EMAIL,
      },
      // Send Test - only for Email tab
      {
        label: t('stats.actions.sendTest'),
        icon: 'far fa-paper-plane',
        command: (usersPagesConfig: any) => {
          // Mark current report as selected
          const reportId = usersPagesConfig.id;
          if (reportId !== null && reportId !== undefined) {
            // eslint-disable-next-line no-param-reassign
            options.selectedReport.value = reportId;
          }
          // Open modal to select mail addresses to send the reports
          store.commit('general/showSendTestModal');
        },
        visible: () => options.activeTab.value === StatsType.EMAIL,
      },
      // Edit
      {
        label: t('stats.actions.edit'),
        icon: 'far fa-edit',
        command: async (usersPagesConfig: any) => {
          if (usersPagesConfig.id) {
            const editStats = async () => {
              await store.dispatch('statsEditor/editPage', {
                pageId: usersPagesConfig.id,
              });
            };

            if (usersPagesConfig.owner === 'user') {
              if (featurePermissionEdit && !featurePermissionEdit.access) {
                store.commit('general/setIsFeatureUnavailableModalVisible', true);
                store.commit('general/setFeatureUnavailableMinPlan', featurePermissionEdit.minimumPlan);
                store.commit('general/setOnAuthorizedClickFunction', editStats);
              } else {
                await editStats();
              }
            } else if (usersPagesConfig.owner === 'system') {
              editStats();
            }
          }
        },
        visible: () => findPermission('stats_report.edit'),
      },
      // Duplicate
      {
        label: t('stats.actions.duplicate'),
        icon: 'far fa-clone',
        command: (usersPagesConfig: any) => {
          options.duplicateConfirmationDialog(usersPagesConfig);
        },
        visible: () => findPermission('stats_report.duplicate'),
      },
      // Rename
      {
        label: t('stats.actions.rename.title'),
        icon: 'fa-light fa-input-text',
        command: (usersPagesConfig: UsersPagesConfigurationUpdateInputItem, rowKey?: string | string[], event?: any) => {
          // For rename action, we need the event from the click to position the overlay
          if (event && usersPagesConfig) {
            options.showRenameOverlay(usersPagesConfig, event);
          }
        },
        visible: () => findPermission('stats_report.rename'),
      },
      // Delete
      {
        label: t('stats.actions.delete'),
        icon: 'far fa-trash',
        command: (usersPagesConfig: any) => {
          options.deleteConfirmationDialog(usersPagesConfig);
        },
        visible: (usersPagesConfig: any) => {
          if (usersPagesConfig.owner === 'system') {
            return false;
          }
          return findPermission('stats_report.delete');
        },
      },
    ];

    // Filter out undefined actions
    return actions.filter((action): action is RowActionOption => action !== undefined);
  });

  return {
    rowActions,
  };
}
